import React, { useState } from 'react'
import Modal from './Modal'
import { useTour } from '../contexts/TourContext'
import { IconDashboard, IconPackage, IconMoney, IconCalendar, IconChart, IconSettings, IconHome } from './Icons'
import FaqAccordion from './FaqAccordion'

export default function TutorialModal({ isOpen, onClose, activePage }) {
  const { startTour } = useTour()
  const [activeTab, setActiveTab] = useState('tours') // 'tours' | 'faq'
  
  const handleSelectTour = (tourType) => {
    startTour(tourType)
    onClose()
  }

  // Список всех туров с описанием
  const tours = [
    {
      id: 'general',
      title: 'Быстрый старт (Общий тур)',
      description: 'Познакомьтесь с основными возможностями платформы за 2 минуты. Идеально для первого знакомства.',
      icon: <IconHome size={22} />,
      recommendedPages: ['dashboard'],
    },
    {
      id: 'dashboard',
      title: 'Дашборд и KPI',
      description: 'Как анализировать ключевые показатели вашего ПВЗ, доходы, удержания и следить за активными сменами.',
      icon: <IconDashboard size={22} />,
      recommendedPages: ['dashboard'],
    },
    {
      id: 'operations',
      title: 'Рабочая смена и операции',
      description: 'Как сотрудники начинают/завершают смены и вносят данные по приемке, выдаче и возвратам коробок.',
      icon: <IconPackage size={22} />,
      recommendedPages: ['operations'],
    },
    {
      id: 'payments',
      title: 'Зарплаты и выплаты',
      description: 'Контроль начислений сотрудников, проведение выплат и оспаривание спорных штрафов.',
      icon: <IconMoney size={22} />,
      recommendedPages: ['payments'],
    },
    {
      id: 'schedule',
      title: 'График смен',
      description: 'Составление расписания, планирование графика и автоматическое распределение смен.',
      icon: <IconCalendar size={22} />,
      recommendedPages: ['schedule'],
    },
    {
      id: 'analytics',
      title: 'Финансовая аналитика P&L',
      description: 'Внесение постоянных и переменных расходов, аналитика чистой прибыли ПВЗ.',
      icon: <IconChart size={22} />,
      recommendedPages: ['analytics'],
    },
    {
      id: 'settings',
      title: 'Настройки платформы',
      description: 'Приглашение сотрудников (инвайты) и настройка автоматической формулы зарплаты.',
      icon: <IconSettings size={22} />,
      recommendedPages: ['settings'],
    },
  ]

  // Сортируем туры так, чтобы рекомендуемый для текущей страницы шел первым!
  const finalSortedTours = [...tours].sort((a, b) => {
    const aRec = a.recommendedPages.includes(activePage) && a.id !== 'general'
    const bRec = b.recommendedPages.includes(activePage) && b.id !== 'general'
    if (aRec && !bRec) return -1
    if (!aRec && bRec) return 1
    
    // Общий тур ставим выше остальных нерекомендуемых
    if (a.id === 'general') return -1
    if (b.id === 'general') return 1
    
    return 0
  })

  if (!isOpen) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Обучение платформе"
      isBottomSheet={true}
    >
      <div style={{ padding: '0 0 16px', maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Табы режима: Интерактивный тур vs База знаний */}
        <div style={{
          display: 'flex',
          background: 'var(--surface-hover, rgba(255, 255, 255, 0.05))',
          padding: '4px',
          borderRadius: '12px',
          marginBottom: '16px',
          gap: '4px',
          border: '1px solid var(--border-color, rgba(255, 255, 255, 0.08))'
        }}>
          <button
            onClick={() => setActiveTab('tours')}
            style={{
              flex: 1,
              padding: '9px 12px',
              borderRadius: '9px',
              border: 'none',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              background: activeTab === 'tours' ? 'var(--wb-primary, #a855f7)' : 'transparent',
              color: activeTab === 'tours' ? '#ffffff' : 'var(--text-secondary, #64748b)',
              transition: 'all 0.2s ease',
            }}
          >
            Интерактивные туры
          </button>
          <button
            onClick={() => setActiveTab('faq')}
            style={{
              flex: 1,
              padding: '9px 12px',
              borderRadius: '9px',
              border: 'none',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              background: activeTab === 'faq' ? 'var(--wb-primary, #a855f7)' : 'transparent',
              color: activeTab === 'faq' ? '#ffffff' : 'var(--text-secondary, #64748b)',
              transition: 'all 0.2s ease',
            }}
          >
            Вопросы и ответы (FAQ)
          </button>
        </div>

        {activeTab === 'faq' ? (
          <FaqAccordion />
        ) : (
          <>
            <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', marginBottom: '16px', lineHeight: '1.4', textAlign: 'center' }}>
              Выберите интерактивный тур по интересующему вас разделу. Я проведу вас по кнопкам и функциям.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {finalSortedTours.map((tour) => {
                const isRecommended = tour.recommendedPages.includes(activePage) && tour.id !== 'general'
                return (
                  <div
                    key={tour.id}
                    onClick={() => handleSelectTour(tour.id)}
                    className={`tour-selection-card ${isRecommended ? 'tour-selection-card--recommended' : ''}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      padding: '16px',
                      borderRadius: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                    }}
                  >
                    <div 
                      className="tour-selection-icon-wrap"
                      style={{
                        width: '42px',
                        height: '42px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: 'all 0.25s ease',
                      }}
                    >
                      {tour.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 800, fontSize: '15px', color: 'var(--text-primary)' }}>{tour.title}</span>
                        {isRecommended && (
                          <span style={{
                            fontSize: '10px',
                            fontWeight: 800,
                            background: 'linear-gradient(135deg, var(--wb-primary), #9333ea)',
                            color: '#fff',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}>
                            Рекомендуется
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: '12.5px', color: 'var(--text-secondary)', marginTop: '4px', lineHeight: '1.4' }}>
                        {tour.description}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>

      <style>{`
        .tour-selection-card {
          background: var(--surface-color, rgba(255, 255, 255, 0.03));
          border: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
        }
        .tour-selection-card:hover {
          transform: translateY(-2px);
          border-color: rgba(168, 85, 247, 0.45) !important;
          background: rgba(168, 85, 247, 0.04) !important;
          box-shadow: 0 6px 20px rgba(168, 85, 247, 0.08);
        }
        .tour-selection-card--recommended {
          background: rgba(168, 85, 247, 0.06) !important;
          border: 1.5px solid rgba(168, 85, 247, 0.35) !important;
        }
        .tour-selection-card--recommended:hover {
          background: rgba(168, 85, 247, 0.08) !important;
          border-color: rgba(168, 85, 247, 0.5) !important;
        }
        .tour-selection-icon-wrap {
          background: var(--surface-hover, rgba(255, 255, 255, 0.06));
          color: var(--text-secondary);
        }
        .tour-selection-card--recommended .tour-selection-icon-wrap {
          background: linear-gradient(135deg, #a855f7, #7c3aed) !important;
          color: #ffffff !important;
          box-shadow: 0 4px 12px rgba(168, 85, 247, 0.25);
        }
        .tour-selection-card:hover .tour-selection-icon-wrap {
          color: #a855f7;
        }
        .tour-selection-card--recommended:hover .tour-selection-icon-wrap {
          color: #ffffff !important;
          transform: scale(1.05);
        }
      `}</style>
    </Modal>
  )
}
